'use client';

import { XIcon } from '@heroicons/react/solid';
import { Avatar, Button, Input } from 'antd';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useRef } from 'react';
import { useRecoilState } from 'recoil';

import { getUserInitials } from '../ChatBox/Message';
import { authModalControl } from '../../atoms/authModalControl';
import { letterColors } from '../../utils/AvatarColor';
import SendIcon from '../../../assets/Icons/SendIcon';

interface Message {
  id: number;
  user: {
    name: string;
    avatar: string;
  };
  text: string;
}

interface Props {
  toggleChat: () => void;
  comments: Message[];
  setState: any;
  sendMessageHandle: () => void;
  handleMessageSummit: () => void;
  message: string;
  isSignedIn: boolean;
  userName?: string;
}

const LiveChat: React.FC<Props> = ({
  toggleChat,
  comments,
  setState,
  sendMessageHandle,
  handleMessageSummit,
  message,
  userName,
  isSignedIn,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [, setVisible] = useRecoilState(authModalControl);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [comments]);
  return (
    <div className='flex flex-col h-full sticky top-0 right-0'>
      <div className='p-4 border-b flex gap-x-4 items-center justify-between  border-[#1C1C1C] bg-black'>
        <div className='flex gap-x-4 items-center'>
          <span className='animate-pulse bg-[#C72707] w-2 h-2 rounded-full'></span>
          <h2 className='text-sm font-semibold'>Live Chat</h2>
        </div>

        <span className='bg-[#121212] ' onClick={toggleChat}>
          <XIcon className='w-4 h-4' />
        </span>
      </div>

      <div
        style={{
          backgroundColor: '#1A1A1A',
          borderRadius: 12,
          overflowY: 'auto',
          scrollbarWidth: 'thin',
          scrollbarColor: '#FFFFFF99 transparent',
        }}
        className='flex-1 overflow-y-auto p-3 md:p-6 bg-[#121212] space-y-5'
      >
        <AnimatePresence>
          {comments?.map((message, index) => {
            return (
              <motion.div
                key={`${message?.id}-${index}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className='flex items-start space-x-3'
              >
                <div className='w-9'>
                  <Avatar
                    size={36}
                    style={{
                      backgroundColor: !message?.user?.avatar
                        ? (letterColors as Record<string, string>)[
                            (userName === message?.user?.name
                              ? 'You'
                              : message?.user?.name
                            )
                              ?.charAt(0)
                              .toUpperCase()
                          ]
                        : '#000000',
                      fontSize: '14px',
                    }}
                    src={`${process.env.NEXT_PUBLIC_IMAGE_BASE_URL_ECOM}/${message?.user?.avatar}`}
                  >
                    {message?.user?.name &&
                      getUserInitials(
                        userName === message?.user?.name
                          ? 'You'
                          : message?.user?.name,
                      )}
                  </Avatar>
                </div>

                <div className='flex gap-y-0.5 flex-col'>
                  <div className='text-xs font-semibold'>
                    {userName === message?.user?.name
                      ? 'You'
                      : message?.user?.name}
                  </div>
                  <div className='text-xs font-[400] '>{message.text}</div>
                </div>
              </motion.div>
            );
          })}
          <div ref={messagesEndRef} />
        </AnimatePresence>
      </div>

      {isSignedIn ? (
        <div className='px-4 py-[15px] md:py-[21px] border-t border-l justify-center w-full border-[#1C1C1C] bg-bgColor flex  items-center text-center'>
          <div className='flex space-x-2 w-full'>
            <Input
              placeholder='Type a message...'
              type='large'
              value={message}
              onChange={(e) =>
                setState((prevState: any) => ({
                  ...prevState,
                  message: e.target.value,
                }))
              }
              onKeyDown={(e) => e.key === 'Enter' && sendMessageHandle()}
              className='bg-[#1C1C1C] border-none h-[36px]  md:h-[48px] rounded-[12px] placeholder:text-[#9F9EA6] placeholder:font-medium text-white'
            />
            <Button
              onClick={handleMessageSummit}
              type='primary'
              disabled={!message}
              className='rounded-full relative flex h-[36px]  md:h-12  disabled:opacity-80 w-[36px]  md:w-12 justify-center items-center shadow-[0px_-2px_4px_0px_#00000040_inset,0px_3px_2px_0px_#FFFFFF17_inset] text-white'
            >
              <SendIcon
                fill='#FF5612'
                width={24}
                height={24}
                className='absolute left-[55%] top-[54%] -translate-x-1/2 -translate-y-1/2 h-6 w-6'
              />
            </Button>
          </div>
        </div>
      ) : (
        <div className='px-4 py-[21px] border-t border-l justify-between  border-[#1C1C1C] bg-bgColor flex  items-center text-center'>
          <p className='text-white text-sm'>You must be signed in to chat.</p>
          <Button
            onClick={() =>
              setVisible((prevState) => ({
                ...prevState,
                login: true,
              }))
            }
            size='large'
            type='primary'
            className='\ text-white text-sm'
          >
            Sign In
          </Button>
        </div>
      )}
    </div>
  );
};

export default LiveChat;
